import React from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { getFieldKey } from '@user/components/Settings/PendingLeadSettings/helpers'
import { useData } from '@user/components/Settings/PendingLeadSettings/hooks/addRequiredFieldDialog/useData'
import { useHandlers } from '@user/components/Settings/PendingLeadSettings/hooks/addRequiredFieldDialog/useHandlers'
import { AddRequiredFieldOption } from '@user/components/Settings/PendingLeadSettings/AddRequiredFieldOption'
import type { IAutoReviewAdditionalField } from '@user/store/autoReviewSettings'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'

export type Props = {
  open: boolean
  leadTypeLabels: string[]
  options: IAutoReviewAdditionalField[]
  added: IAutoReviewAdditionalField[]
  onCancel: () => void
  onAdd: (selection: IAutoReviewAdditionalField[]) => void
}

export const AddRequiredFieldDialog = (props: Props) => {
  const { open, leadTypeLabels, options, added, onCancel, onAdd } = props

  const data = useData({ open, added })
  const { isPicked, isAdded, submitDisabled } = data
  const { handleToggle, handleSubmit } = useHandlers(data, { onAdd })

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      data-test-id="pending-lead-add-field-dialog"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e2e5ea',
          mb: '20px',
        }}
      >
        <Typography variant={'subtitle3'}>Add Field</Typography>

        <IconButton
          onClick={onCancel}
          size={'small'}
          sx={{
            cursor: 'pointer',
          }}
          data-testid="action-modal-close-btn"
          data-test-id="attachmentsDrawerCloseBtn"
        >
          <CloseIcon fontSize={'small'} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: '24px' }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select any other fields the pending lead must have before it can be
          auto-reviewed.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          These fields will apply to {leadTypeLabels.join(', ')} only.
        </Alert>

        {options.length === 0 ? (
          <Typography
            variant="body2"
            data-test-id="pending-lead-add-field-empty"
          >
            No additional fields are available to add.
          </Typography>
        ) : (
          <Stack
            maxHeight={'300px'}
            overflow="auto"
            sx={{
              padding: '10px 26px',
              border: '1px solid  #e2e5ea',
              borderRadius: '8px',
            }}
          >
            {options.map((option) => (
              <AddRequiredFieldOption
                key={getFieldKey(option)}
                label={option.fieldName}
                checked={isPicked(option)}
                alreadyAdded={isAdded(option)}
                onToggle={handleToggle(option)}
                testId={`pending-lead-add-field-option-${getFieldKey(option)}`}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: '1px solid #e2e5ea',
          px: '24px',
          py: '16px',
        }}
      >
        <Button
          variant="text"
          onClick={onCancel}
          data-test-id="pending-lead-add-field-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={submitDisabled}
          onClick={handleSubmit}
          data-test-id="pending-lead-add-field-submit"
        >
          Add Selected
        </Button>
      </DialogActions>
    </Dialog>
  )
}

AddRequiredFieldDialog.displayName = 'AddRequiredFieldDialog'
